import {
  Button,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@fountain/fountain-ui-components';
import { Typography } from '@material-ui/core';
import React, { FC, useRef } from 'react';

import { useStyles } from './styles';

let sequence = 0;

/**
 * The id shared by the heading and the dialog that points at it.
 *
 * Minted here rather than accepted from the caller: `aria-labelledby` is a
 * space-separated ID LIST, so a translated title handed in as the value
 * tokenises into ids that do not exist and the dialog is left with no
 * accessible name. Owning the id makes that shape unrepresentable.
 *
 * React 16 has no `useId`, so this is the `useRef` counter
 * `OfferLetterTemplates/Editor/FieldLabel` already uses — recruiter-ui does not
 * SSR, so the value never has to match a server render.
 */
const useHeadingId = (): string =>
  useRef(`confirmation-modal-title-${(sequence += 1)}`).current;

export interface ConfirmationModalProps {
  bodyContent: React.ReactNode;
  cancelButtonContent: React.ReactNode;
  /**
   * Omit it for a notice with nothing to confirm: the footer then carries the
   * cancel button alone, and `onConfirm` is never called.
   */
  confirmButtonContent?: React.ReactNode;
  isOpen?: boolean;
  isLoading?: boolean;
  disabled?: boolean;
  maxWidth?: number;
  onCancel: (event: React.MouseEvent<HTMLButtonElement>) => void;
  // For consumers that need to differentiate between clicking the secondary "cancel" button and
  // a "close modal" action (i.e. clicking the header 'X', or clicking outside the modal).
  // If `onClose` is provided, it will override the the "close modal" behavior.
  onClose?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onConfirm: (event: React.MouseEvent<HTMLButtonElement>) => void;
  title: string;
  negative?: boolean;
  blurOverlay?: boolean;
  /**
   * Icon for the round well beside the title. Omitting it leaves the header
   * text-only, which is what every caller had before the prop existed.
   *
   * `ModalHeader` hands a function icon its own `color` — `false` on a modal
   * that is not `negative`, which trips the icon's `color: string` propType.
   * Wrap the icon (`() => <Icon color={token} />`) to pin the color instead.
   */
  headerIcon?: React.FC | string;
}

export const ConfirmationModal: FC<ConfirmationModalProps> = ({
  bodyContent,
  cancelButtonContent,
  confirmButtonContent,
  isOpen = true,
  isLoading = false,
  disabled = false,
  maxWidth = 400,
  onCancel,
  onClose,
  onConfirm,
  title,
  negative = false,
  blurOverlay = true,
  headerIcon,
}) => {
  const styles = useStyles();
  const headingId = useHeadingId();

  return (
    <Modal
      ariaLabelledBy={headingId}
      fullScreenOnMobile
      onClose={onClose ?? onCancel}
      open={isOpen}
      blurOverlay={blurOverlay}
      maxWidth={maxWidth}
    >
      {/* ModalHeader renders `id`, Modal renders `aria-labelledby`, and both
          take the same minted value — see `useHeadingId`. */}
      <ModalHeader
        ariaLabelledBy={headingId}
        className={styles.header}
        Icon={headerIcon}
        negative={negative}
        onClose={onClose ?? onCancel}
        showIcon={Boolean(headerIcon)}
      >
        {title}
      </ModalHeader>
      <ModalContent>
        <Typography className={styles.bodyText} variant="body2">
          {bodyContent}
        </Typography>
      </ModalContent>
      <ModalFooter className={styles.footer}>
        <Button
          onClick={onCancel}
          size="small"
          type="secondary"
          disabled={disabled}
        >
          {cancelButtonContent}
        </Button>
        {confirmButtonContent != null && (
          <Button
            onClick={onConfirm}
            size="small"
            negative={negative}
            disabled={disabled}
            isLoading={isLoading}
          >
            {confirmButtonContent}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};
